import React, {
  useEffect,
  useState,
} from "react";

import {
  PaymentScheduleResponse,
  PaymentScheduleBulkUpsert,
  PaymentScheduleUpdate,
} from "@/api";

import PaymentScheduleView from "./PaymentScheduleView";
import PaymentScheduleEdit from "./PaymentScheduleEdit";

import {
  AdminOrderUpdateService,
} from "../../service/adminOrderUpdateServices";

export interface PaymentScheduleSectionProps {
  finalAmount: number;

  schedules: PaymentScheduleResponse[];

  onChange: (
    schedules: PaymentScheduleResponse[],
  ) => void;
}

/*
 * ==================================================
 * NORMALIZE FOR DIRTY CHECK
 * ==================================================
 *
 * Only fields that are persisted by the update
 * request should participate in the comparison.
 */
function normalizeSchedule(
  schedule: PaymentScheduleResponse,
) {
  return {
    id: schedule.id ?? null,

    sequence:
      schedule.sequence ?? null,

    percentage:
      schedule.percentage ?? null,

    label:
      schedule.label ?? null,

    due_date:
      schedule.due_date ?? null,

    grace_period_days:
      schedule.grace_period_days ?? null,

    amount:
      schedule.amount != null
        ? Number(schedule.amount)
        : null,

    notes:
      schedule.notes ?? null,
  };
}

/*
 * ==================================================
 * DIRTY CHECK
 * ==================================================
 */
function areSchedulesDirty(
  original: PaymentScheduleResponse[],
  draft: PaymentScheduleResponse[],
): boolean {
  const normalizedOriginal =
    original.map(normalizeSchedule);

  const normalizedDraft =
    draft.map(normalizeSchedule);

  return (
    JSON.stringify(normalizedOriginal) !==
    JSON.stringify(normalizedDraft)
  );
}

export default function PaymentScheduleSection({
  finalAmount,
  schedules,
  onChange,
}: PaymentScheduleSectionProps) {
  const [editing, setEditing] =
    useState(false);

  const [draftSchedules, setDraftSchedules] =
    useState<PaymentScheduleResponse[]>(
      schedules ?? [],
    );

  const [saving, setSaving] =
    useState(false);

  const [error, setError] =
    useState<string | null>(null);

  /*
   * ==================================================
   * SYNC FROM PARENT
   * ==================================================
   */

  useEffect(() => {
    if (!editing) {
      setDraftSchedules(
        (schedules ?? []).map(
          (schedule) => ({
            ...schedule,
          }),
        ),
      );
    }
  }, [schedules, editing]);

  /*
   * ==================================================
   * EDIT
   * ==================================================
   */

  const handleEdit = () => {
    setDraftSchedules(
      (schedules ?? []).map(
        (schedule) => ({
          ...schedule,
        }),
      ),
    );

    setError(null);
    setEditing(true);
  };

  /*
   * ==================================================
   * SAVE
   * ==================================================
   */

  const handleSave = async () => {
    try {
      setError(null);

      /*
       * ----------------------------------------------
       * Check whether anything actually changed.
       * ----------------------------------------------
       */

      const dirty = areSchedulesDirty(
        schedules ?? [],
        draftSchedules,
      );

      if (!dirty) {
        console.log(
          "PAYMENT SCHEDULE: No changes detected. Skipping update.",
        );

        setEditing(false);
        return;
      }

      setSaving(true);

      /*
       * ----------------------------------------------
       * Invoice ID
       * ----------------------------------------------
       */

      const invoiceId =
        draftSchedules[0]?.invoice_id ??
        schedules?.[0]?.invoice_id;

      if (!invoiceId) {
        throw new Error(
          "Invoice ID is missing from payment schedule.",
        );
      }

      /*
       * ----------------------------------------------
       * Build Update DTO
       * ----------------------------------------------
       *
       * Important:
       *
       * draftSchedules is the authoritative edited
       * collection.
       *
       * Therefore:
       *
       * 1 record -> 2 records
       *   #20 update
       *   #21 create
       *
       * 2 records -> 1 record
       *   #20 update
       *   #21 omitted
       *   backend deletes #21
       */

      const scheduleUpdates:
        PaymentScheduleUpdate[] =
        draftSchedules.map(
          (schedule) => ({
            id:
              schedule.id &&
              schedule.id > 0
                ? schedule.id
                : null,

            sequence:
              Number(schedule.sequence),

            percentage:
              Number(schedule.percentage),

            label:
              schedule.label ?? null,

            due_date:
              schedule.due_date ?? null,

            grace_period_days:
              Number(
                schedule.grace_period_days ?? 0,
              ),

            amount:
              Number(
                schedule.amount ?? 0,
              ),

            notes:
              schedule.notes ?? null,
          }),
        );

      const payload:
        PaymentScheduleBulkUpsert = {
          schedules:
            scheduleUpdates,
        };

      console.log(
        "PAYMENT SCHEDULE UPDATE PAYLOAD:",
        JSON.stringify(
          payload,
          null,
          2,
        ),
      );

      /*
       * ----------------------------------------------
       * Persist
       * ----------------------------------------------
       */

      const updatedSchedules =
        await AdminOrderUpdateService
          .upsert_payment_schedules(
            invoiceId,
            payload,
          );

      console.log(
        "PAYMENT SCHEDULE UPDATED:",
        JSON.stringify(
          updatedSchedules,
          null,
          2,
        ),
      );

      /*
       * ----------------------------------------------
       * Backend response is authoritative.
       * ----------------------------------------------
       */

      onChange(updatedSchedules);

      /*
       * ----------------------------------------------
       * Leave edit mode only after successful save.
       * ----------------------------------------------
       */

      setEditing(false);

    } catch (err) {
      console.error(
        "Failed to update payment schedules",
        err,
      );

      setError(
        err instanceof Error
          ? err.message
          : "Failed to update payment schedules.",
      );
    } finally {
      setSaving(false);
    }
  };

  /*
   * ==================================================
   * CANCEL
   * ==================================================
   */

  const handleCancel = () => {
    setDraftSchedules(
      (schedules ?? []).map(
        (schedule) => ({
          ...schedule,
        }),
      ),
    );

    setError(null);
    setEditing(false);
  };

  return (
    <section className="rounded-xl border bg-white p-6">

      {editing ? (
        <>
          <SectionHeader
            title="Payment Schedule"
            description="Edit the invoice payment schedule."
            editing
            saving={saving}
            onCancel={handleCancel}
            onSave={() => void handleSave()}
          />

          <PaymentScheduleEdit
            schedules={draftSchedules}
            onChange={setDraftSchedules}
            finalAmount={finalAmount}
          />

          {error && (
            <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}
        </>
      ) : (
        <>
          <SectionHeader
            title="Payment Schedule"
            description="Review the invoice payment schedule."
            onEdit={handleEdit}
          />

          <PaymentScheduleView
            finalAmount={finalAmount}
            schedules={schedules ?? []}
          />
        </>
      )}
    </section>
  );
}

/*
 * ==================================================
 * HEADER
 * ==================================================
 */

interface SectionHeaderProps {
  title: string;
  description: string;

  editing?: boolean;
  saving?: boolean;

  onEdit?: () => void;
  onCancel?: () => void;
  onSave?: () => void;
}

function SectionHeader({
  title,
  description,
  editing = false,
  saving = false,
  onEdit,
  onCancel,
  onSave,
}: SectionHeaderProps) {
  return (
    <div className="mb-6 flex items-start justify-between gap-4">

      <div>
        <h2 className="text-lg font-semibold">
          {title}
        </h2>

        <p className="mt-1 text-sm text-gray-500">
          {description}
        </p>
      </div>

      {editing ? (
        <div className="flex shrink-0 gap-2">

          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="rounded-md border px-3 py-2 text-sm font-medium disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="rounded-md bg-black px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {saving
              ? "Saving..."
              : "Save"}
          </button>

        </div>
      ) : (
        <button
          type="button"
          onClick={onEdit}
          className="rounded-md border px-3 py-2 text-sm font-medium"
        >
          Edit
        </button>
      )}
    </div>
  );
}